// src/pages/admin/AdminListings.js
// Gestion des annonces (admin) connectée à l'API - NE PAS MODIFIER

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ROUTES from '../../constants/routes';
import { LISTING_STATUS, LISTING_STATUS_LABELS, LISTING_STATUS_COLORS } from '../../constants/listingStatus';
import { formatPrice } from '../../utils/formatPrice';
import apiClient from '../../api/client';

const AdminListings = () => {
  const navigate = useNavigate();
  const [allListings, setAllListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const loadListings = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiClient.get('/endpoints/listings.php?action=list&status=all&limit=200');
      setAllListings(result.listings || []);
    } catch (err) {
      setAllListings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadListings();
  }, [loadListings]);

  const statusFilters = [
    { id: 'all', label: 'Toutes' },
    { id: 'published', label: 'Publiées' },
    { id: 'pending', label: 'En attente' },
    { id: 'draft', label: 'Brouillons' },
    { id: 'rejected', label: 'Rejetées' },
    { id: 'disabled', label: 'Désactivées' },
  ];

  const filteredListings = allListings.filter((l) => {
    const matchStatus = filter === 'all' || l.status === filter;
    const matchSearch =
      !search ||
      l.title.toLowerCase().includes(search.toLowerCase()) ||
      l.quartier.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  const handleStatusChange = async (listingId, newStatus) => {
    try {
      await apiClient.put(`/endpoints/listings.php?action=update-status&id=${listingId}`, { status: newStatus });
      setAllListings((prev) =>
        prev.map((l) => (l.id === listingId ? { ...l, status: newStatus } : l))
      );
    } catch (err) {
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const handleDelete = async (listingId) => {
    if (!window.confirm('Supprimer définitivement cette annonce ?')) return;
    try {
      await apiClient.delete(`/endpoints/listings.php?action=delete&id=${listingId}`);
      setAllListings((prev) => prev.filter((l) => l.id !== listingId));
    } catch (err) {
      alert('Erreur lors de la suppression');
    }
  };

  const styles = {
    container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', maxWidth: '900px', margin: '0 auto', backgroundColor: '#0A0E1A' },
    header: { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', position: 'sticky', top: 0, backgroundColor: '#0A0E1A', zIndex: 50 },
    backButton: { background: 'none', border: 'none', color: '#FFFFFF', fontSize: '20px', cursor: 'pointer' },
    title: { fontSize: '20px', fontWeight: '700', color: '#FFFFFF', flex: 1 },
    addButton: { padding: '8px 16px', backgroundColor: '#FFD700', border: 'none', borderRadius: '8px', color: '#0A0E1A', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
    searchInput: { margin: '16px 20px 0', padding: '12px 16px', backgroundColor: '#111827', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#FFFFFF', fontSize: '14px', outline: 'none' },
    filters: { display: 'flex', gap: '8px', overflowX: 'auto', padding: '16px 20px', scrollbarWidth: 'none' },
    filterBtn: { padding: '8px 14px', borderRadius: '20px', backgroundColor: '#162033', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#A0AEC0', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
    filterBtnActive: { backgroundColor: 'rgba(255, 215, 0, 0.15)', border: '1px solid rgba(255, 215, 0, 0.3)', color: '#FFD700' },
    list: { display: 'flex', flexDirection: 'column', gap: '12px', padding: '0 20px 40px' },
    card: { display: 'flex', gap: '12px', padding: '12px', backgroundColor: '#162033', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.1)' },
    cardImage: { width: '70px', height: '70px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0, backgroundColor: '#111827' },
    cardBody: { flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' },
    cardTitle: { fontSize: '14px', fontWeight: '600', color: '#FFFFFF' },
    cardLocation: { fontSize: '12px', color: '#A0AEC0' },
    cardPrice: { fontSize: '13px', fontWeight: '600', color: '#FFD700' },
    cardFooter: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '4px' },
    statusBadge: { display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' },
    actions: { display: 'flex', gap: '6px' },
    actionBtn: { padding: '5px 10px', borderRadius: '6px', border: 'none', fontSize: '11px', fontWeight: '600', cursor: 'pointer' },
    approveBtn: { backgroundColor: 'rgba(72, 187, 120, 0.15)', color: '#48BB78' },
    editBtn: { backgroundColor: 'rgba(66, 153, 225, 0.15)', color: '#4299E1' },
    deleteBtn: { backgroundColor: 'rgba(245, 101, 101, 0.15)', color: '#F56565' },
    empty: { textAlign: 'center', padding: '48px', color: '#A0AEC0', fontSize: '14px' },
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Gestion des annonces | Fynho Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(ROUTES.ADMIN_DASHBOARD)}>←</button>
        <div style={styles.title}>Annonces</div>
        <button style={styles.addButton} onClick={() => navigate(ROUTES.ADMIN_ADD_LISTING)}>
          + Ajouter
        </button>
      </div>

      <input
        type="text"
        style={styles.searchInput}
        placeholder="Rechercher une annonce..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div style={styles.filters} className="hide-scrollbar">
        <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; }`}</style>
        {statusFilters.map((f) => (
          <button
            key={f.id}
            style={{ ...styles.filterBtn, ...(filter === f.id ? styles.filterBtnActive : {}) }}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div style={styles.list}>
        {loading ? (
          <div style={styles.empty}>Chargement...</div>
        ) : filteredListings.length === 0 ? (
          <div style={styles.empty}>Aucune annonce trouvée</div>
        ) : (
          filteredListings.map((listing) => (
            <div key={listing.id} style={styles.card}>
              <img
                src={listing.images?.[0] || '/images/placeholder.png'}
                alt={listing.title}
                style={styles.cardImage}
              />
              <div style={styles.cardBody}>
                <div style={styles.cardTitle}>{listing.title}</div>
                <div style={styles.cardLocation}>📍 {listing.quartier}, {listing.city}</div>
                <div style={styles.cardPrice}>{formatPrice(listing.price)}</div>
                <div style={styles.cardFooter}>
                  <span
                    style={{
                      ...styles.statusBadge,
                      backgroundColor: `${LISTING_STATUS_COLORS[listing.status]}22`,
                      color: LISTING_STATUS_COLORS[listing.status],
                    }}
                  >
                    {LISTING_STATUS_LABELS[listing.status]}
                  </span>
                  <div style={styles.actions}>
                    {listing.status === 'pending' && (
                      <button
                        style={{ ...styles.actionBtn, ...styles.approveBtn }}
                        onClick={() => handleStatusChange(listing.id, LISTING_STATUS.PUBLISHED)}
                      >
                        ✓ Publier
                      </button>
                    )}
                    <button
                      style={{ ...styles.actionBtn, ...styles.editBtn }}
                      onClick={() => navigate(`/admin/listings/edit/${listing.id}`)}
                    >
                      Modifier
                    </button>
                    <button
                      style={{ ...styles.actionBtn, ...styles.deleteBtn }}
                      onClick={() => handleDelete(listing.id)}
                    >
                      Suppr.
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminListings;